import { type Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingIcon,
  Textarea,
} from '@kalos/ui';
import { LockIcon, PinIcon } from 'lucide-react';
import { type FC } from 'react';

import { useEditMemoForm } from './useEditMemoForm';

interface EditMemoFormProps {
  memo: Memo;
  onClose?: () => void;
  onSuccess?: () => void;
  /**
   * The desktop project record's layout: labelled fields with no icons, "The customer can see
   * this note" in place of "Internal only" (the same field, inverted), and Save note. Leave it
   * out for the layout everywhere else.
   */
  desktopLayout?: boolean;
  /** Tells a surrounding dialog when the save is running, so it can't be closed mid-save. */
  onBusyChange?: (isBusy: boolean) => void;
}

export const EditMemoForm: FC<EditMemoFormProps> = ({
  memo,
  onClose,
  onSuccess,
  desktopLayout = false,
  onBusyChange,
}) => {
  const { form, handleSubmit, isPending } = useEditMemoForm({
    memo,
    onClose,
    onSuccess,
    onBusyChange,
  });

  if (desktopLayout) {
    return (
      <FormContext {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Note</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    disabled={isPending}
                    maxLength={5000}
                    autoGrow={{ minRows: 5, maxRows: 14 }}
                    className="bg-card"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-3">
            <FormField
              control={form.control}
              name="isPinned"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Pin this note to the top of the list
                  </FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isInternal"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={!field.value}
                      onCheckedChange={(checked) => field.onChange(checked !== true)}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">The customer can see this note</FormLabel>
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end gap-2">
            {onClose && (
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isPending}>
              {isPending && <LoadingIcon className="h-4 w-4" />}
              {isPending ? 'Saving note…' : 'Save note'}
            </Button>
          </div>
        </form>
      </FormContext>
    );
  }

  return (
    <FormContext {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5 py-6">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Note</FormLabel>
              <FormMessage />
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter your note..."
                  disabled={isPending}
                  maxLength={5000}
                  className="min-h-[120px] resize-y"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex flex-wrap gap-x-6">
          <FormField
            control={form.control}
            name="isPinned"
            render={({ field }) => (
              <FormItem className="touch:min-h-11 flex items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isPending}
                  />
                </FormControl>
                <FormLabel className="touch:min-h-11 flex cursor-pointer items-center gap-1 font-normal">
                  <PinIcon className="h-4 w-4" />
                  Pin this note
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isInternal"
            render={({ field }) => (
              <FormItem className="touch:min-h-11 flex items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isPending}
                  />
                </FormControl>
                <FormLabel className="touch:min-h-11 flex cursor-pointer items-center gap-1 font-normal">
                  <LockIcon className="h-4 w-4" />
                  Internal only
                </FormLabel>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-3">
          {onClose && (
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </FormContext>
  );
};
